import React, { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { BetaFeature } from '../../@types/BetaFeature.type';
import { useAuth } from '../../component/context/AuthContext/AuthContext';
import { BetaThemeProvider, useBetaTheme } from '../context/BetaThemeContext';
import BetaHeader from './BetaHeader';
import BetaFooter from './BetaFooter';
import '../styles/BetaLayout.scss';

interface BetaLayoutProps {
  features: BetaFeature[];
}

const BetaShell: React.FC<BetaLayoutProps> = ({ features }) => {
  const { theme } = useBetaTheme();
  const { userData, signOutRedirect } = useAuth();
  const location = useLocation();

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette:
          theme === 'dark'
            ? {
                mode: 'dark',
                primary: { main: '#82ccff' },
                background: { default: '#1d212c', paper: '#2d3142' },
              }
            : {
                mode: 'light',
                primary: { main: '#297eb8', dark: '#0b3865' },
                background: { default: '#edeff3', paper: '#ffffff' },
              },
        typography: {
          fontFamily: "'AS Circular', 'Helvetica Neue', Arial, sans-serif",
          button: { textTransform: 'none', fontWeight: 600 },
        },
        shape: { borderRadius: 4 },
      }),
    [theme],
  );

  const currentFeature = features.find((feature) =>
    location.pathname.toLowerCase().startsWith(feature.path.toLowerCase()),
  );

  const handleLogout = signOutRedirect
    ? () => {
        signOutRedirect().catch(() => undefined);
      }
    : undefined;

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline enableColorScheme />
      <div className="beta-app" data-theme={theme} data-testid="beta-app">
        <BetaHeader
          userName={userData?.profile?.name}
          userEmail={userData?.profile?.email}
          pageTitle={currentFeature?.label ?? 'JetStream'}
          features={features}
          onLogout={handleLogout}
        />
        <main className="beta-main">
          <Outlet />
        </main>
        <BetaFooter />
      </div>
    </ThemeProvider>
  );
};

const BetaLayout: React.FC<BetaLayoutProps> = ({ features }) => (
  <BetaThemeProvider>
    <BetaShell features={features} />
  </BetaThemeProvider>
);

export default BetaLayout;
